import React, { useEffect, useState } from "react";
import { useLang } from "../LangContext";
import { Menu, X, Globe } from "lucide-react";
import { LOGO_URL } from "../assets/brand";

export const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#mission", label: t.nav.mission },
    { href: "#universes", label: t.nav.universes },
    { href: "#follow-me", label: t.nav.followMe },
    { href: "#founders", label: t.nav.founders },
    { href: "#upcoming", label: t.nav.upcoming },
  ];

  const handleLink = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <nav
          className={`flex items-center justify-between rounded-full pl-5 pr-3 py-3 transition-all duration-300 ${
            scrolled
              ? "glass-strong shadow-[0_10px_40px_-10px_rgba(139,92,246,0.35)]"
              : "bg-transparent"
          }`}
        >
          <a
            href="#top"
            onClick={(e) => handleLink(e, "#top")}
            className="flex items-center gap-3 group"
            data-testid="navbar-logo"
          >
            <div className="relative">
              <div className="w-11 h-11 rounded-2xl overflow-hidden bg-white shadow-lg shadow-fuchsia-500/30 transition-transform group-hover:rotate-[-6deg] ring-1 ring-white/20">
                <img
                  src={LOGO_URL}
                  alt="Bac à jeux"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -inset-1 rounded-2xl bg-baj-grad blur-md opacity-40 -z-10" />
            </div>
            <span className="font-display text-xl font-extrabold tracking-tight hidden sm:inline">
              Bac à jeux
            </span>
          </a>

          <ul className="hidden lg:flex items-center gap-1">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => handleLink(e, l.href)}
                  className="px-4 py-2 rounded-full text-sm font-medium text-slate-200/90 hover:text-white hover:bg-white/5 transition-colors"
                  data-testid={`nav-link-${l.href.replace("#", "")}`}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <div
              className="hidden sm:flex items-center bg-white/5 border border-white/10 rounded-full p-1"
              data-testid="lang-selector"
            >
              <button
                type="button"
                onClick={() => setLang("fr")}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition-all ${
                  lang === "fr" ? "bg-baj-grad text-white shadow-md" : "text-slate-300 hover:text-white"
                }`}
                data-testid="lang-fr-btn"
              >
                FR
              </button>
              <button
                type="button"
                onClick={() => setLang("en")}
                className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition-all ${
                  lang === "en" ? "bg-baj-grad text-white shadow-md" : "text-slate-300 hover:text-white"
                }`}
                data-testid="lang-en-btn"
              >
                EN
              </button>
            </div>

            <a
              href="#follow-me"
              onClick={(e) => handleLink(e, "#follow-me")}
              className="hidden md:inline-flex btn-primary text-sm py-2.5 px-5"
              data-testid="nav-cta"
            >
              {t.nav.cta}
            </a>

            <button
              type="button"
              className="lg:hidden p-2.5 rounded-full bg-white/5 border border-white/10 text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </nav>

        {open && (
          <div
            className="lg:hidden mt-3 glass-strong rounded-3xl p-5 space-y-1"
            data-testid="mobile-menu"
          >
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleLink(e, l.href)}
                className="block px-4 py-3 rounded-2xl text-base font-semibold text-slate-100 hover:bg-white/10"
              >
                {l.label}
              </a>
            ))}
            <div className="flex items-center justify-between pt-3 mt-3 border-t border-white/10">
              <div className="flex items-center gap-2 text-slate-300 text-sm">
                <Globe className="w-4 h-4" />
                <span>FR / EN</span>
              </div>
              <div className="flex items-center bg-white/5 border border-white/10 rounded-full p-1">
                <button
                  type="button"
                  onClick={() => setLang("fr")}
                  className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${
                    lang === "fr" ? "bg-baj-grad text-white" : "text-slate-300"
                  }`}
                  data-testid="lang-fr-btn-mobile"
                >
                  FR
                </button>
                <button
                  type="button"
                  onClick={() => setLang("en")}
                  className={`px-3 py-1 text-xs font-bold uppercase rounded-full ${
                    lang === "en" ? "bg-baj-grad text-white" : "text-slate-300"
                  }`}
                  data-testid="lang-en-btn-mobile"
                >
                  EN
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
